import { Component, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import {
  DeployChannelStats,
  DeployCountKind,
  DeployPlanConfigItem
} from '@core/models/deploy-plan-config.model';
import { PlanDeployStatus } from '@core/models/plan.model';
import { ICONS } from '@core/services/icons/icons.service';
import { getChannelMeta } from '@core/utils/deploy-channel.utils';

import {
  StatusPillComponent,
  StatusPillVariant
} from '@shared/components/status-pill/status-pill.component';

/** One badge on a channel row: a translation key, its parameters, and how it reads. */
interface ChannelPill {
  variant: StatusPillVariant;
  key: string;
  params?: Record<string, number>;
}

@Component({
  selector: 'app-deploy-channel-list',
  imports: [TranslatePipe, StatusPillComponent],
  templateUrl: './deploy-channel-list.component.html',
  styles: ``
})
export class DeployChannelListComponent {
  readonly configs = input.required<DeployPlanConfigItem[]>();
  readonly selectedId = input.required<string | null>();
  readonly statusByConfigId = input.required<ReadonlyMap<string, PlanDeployStatus>>();
  readonly statsByConfigId = input.required<ReadonlyMap<string, DeployChannelStats>>();

  readonly selectConfig = output<DeployPlanConfigItem>();

  protected readonly ICONS = ICONS;
  protected readonly getChannelMeta = getChannelMeta;

  /**
   * Every badge a row wears, in order. Assembled here rather than in the template because the
   * rules interact, and each replacement is the more precise badge displacing the vaguer one:
   * the counts replace "Success", since a green verdict beside "73 of 111 sent" reads as though
   * the other 38 were still coming, and the red "N failed" replaces "Error", which restates the
   * same fact without the number.
   */
  protected pillsFor(item: DeployPlanConfigItem): ChannelPill[] {
    const pills: ChannelPill[] = [];
    const status = this.statusByConfigId().get(item.id);
    const counts = this.countsFor(item.id);

    if (status !== undefined) {
      // A deploy still running has no counts to report, and the previous deploy's would
      // describe a send this one is superseding.
      const settled = status.status !== 'PROCESSING';
      const reported = settled ? counts : null;
      const failedCount = reported?.failedCount ?? 0;

      // "Error" is withheld only when the failing messages are counted beside it: the red
      // "21 failed" is that badge with a number on it. It is kept when nothing per message
      // failed, which is the whole of a channel-level failure — a refused SFTP connection, a
      // missing secret, a deploy that died before its send loop — where no other pill would
      // report that anything went wrong at all.
      const displacedByFailures = status.status === 'ERROR' && failedCount > 0;

      if (reported !== null && status.status === 'SUCCESS') {
        pills.push(this.countPill(reported));
      } else {
        if (!displacedByFailures) {
          pills.push({
            variant: this.statusVariant(status.status),
            key: this.statusLabelKey(status.status)
          });
        }
        // No `sentCount > 0` guard: `countsFor` already withholds a count nobody recorded, so
        // the only thing such a guard suppressed was a *known* zero — "0 of 111 sent" on a
        // deploy that failed outright, which is the one row most worth reporting.
        if (reported !== null) pills.push(this.countPill(reported));
      }

      if (failedCount > 0) {
        pills.push({
          variant: 'danger',
          key: 'features.plan.deploy.status.failed_count',
          params: { count: failedCount }
        });
      }
    }

    // A switched-off channel keeps its last deploy badge: the send it reports really happened,
    // and hiding it would read as though the history were lost too.
    if (!item.isEnabled) {
      pills.push({ variant: 'neutral', key: 'features.plan.deploy.status.disabled' });
    }

    return pills;
  }

  /**
   * What a row has to say about how much got out, or `null` when it has nothing.
   *
   * A total is a per-message notion whatever else is known, so it alone earns a ratio. Without
   * one, only a channel that counts messages has anything left to report: EMAIL, WEBHOOK and
   * GAEL send one payload per configuration, and "1 of 1 sent" would be noise dressed as
   * precision. Branching on the counts rather than on the channel means a channel that gains
   * per-message sends is covered with no table to update here.
   */
  private countsFor(configId: string): DeployChannelStats | null {
    const stats = this.statsByConfigId().get(configId);
    if (stats === undefined) return null;
    if (stats.totalMessages !== null) return stats;
    if (stats.countKind === DeployCountKind.PER_MESSAGE && stats.sentCount > 0)
      return stats;
    return null;
  }

  private countPill(counts: DeployChannelStats): ChannelPill {
    if (counts.totalMessages === null) {
      // A deploy recorded before GBE kept the denominator: with nothing to divide by, neither
      // "all out" nor "partly out" can be claimed, so the number is stated without a verdict
      // on it. It still replaces "Success", which would claim more than is known.
      return {
        variant: 'neutral',
        key: 'features.plan.deploy.status.sent_count',
        params: { count: counts.sentCount }
      };
    }
    return {
      // `>=` rather than `===`: the counts are cumulative per plan while the total comes from
      // the latest deploy, so a channel whose candidates shrank must not read as partial.
      variant: counts.sentCount >= counts.totalMessages ? 'success' : 'warning',
      key: 'features.plan.deploy.status.sent_of_total',
      params: { sent: counts.sentCount, total: counts.totalMessages }
    };
  }

  private statusVariant(status: PlanDeployStatus['status']): StatusPillVariant {
    if (status === 'SUCCESS') return 'success';
    if (status === 'ERROR') return 'danger';
    return 'warning';
  }

  /**
   * "Success" rather than the "OK" a message row wears: this badge reports the *job*, and it is
   * reached only where no count could displace it — a channel that sends one payload, so a green
   * verdict on the whole deploy is the whole truth. The row's "OK" is a readiness on a message
   * nobody has sent yet, which is why the two must not share a label or a colour.
   */
  private statusLabelKey(status: PlanDeployStatus['status']): string {
    if (status === 'SUCCESS') return 'features.plan.deploy.status.success';
    if (status === 'ERROR') return 'features.plan.deploy.status.error';
    return 'features.plan.deploy.status.processing';
  }
}
